/**
 * Карточка переписки — API-хуки.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { client } from './client';
import type { ConversationSource } from '../lib/inbox-id';

export interface SummaryContent {
  topic: string;
  clientWants: string;
  agreed: string[];
  openQuestions: string[];
  nextStep: string;
}

export type SummaryStatus = 'idle' | 'queued' | 'running' | 'failed';

export interface ConversationSummaryResponse {
  summary: SummaryContent | null;
  status: SummaryStatus;
  lastError?: string | null;
  generatedAt?: string | null;
  model?: string | null;
  coveredMessageCount?: number;
  /** Порог устаревания приходит с бэкенда: правило одно на систему */
  staleAfter?: number;
}

export function useConversationSummary(
  source: ConversationSource | null
): UseQueryResult<ConversationSummaryResponse> {
  return useQuery({
    queryKey: ['conversation-summary', source?.sourceType, source?.sourceId],
    enabled: source !== null,
    queryFn: async () => {
      const { data } = await client.get<ConversationSummaryResponse>('/conversations/summary', {
        params: source!,
      });
      return data;
    },
    // Пока идёт сборка — спрашиваем часто, потом успокаиваемся.
    // Иначе оператор смотрит на крутилку и не знает, живая она или зависла
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? 2_000 : false;
    },
  });
}

/**
 * Отказ сборки — словами оператора, а не кодом ответа.
 *
 * Молчащая кнопка обходится дороже любого текста: 30.08 четыре нажатия
 * подряд вернули 404, и на экране не изменилось ничего — поломку искали
 * в карточке, хотя ломался инбокс.
 */
export function refreshErrorText(error: unknown): string {
  if (!isAxiosError(error)) {
    return 'Не получилось отправить запрос. Попробуйте ещё раз.';
  }
  if (error.response === undefined) {
    return 'Нет связи с сервером. Проверьте соединение и попробуйте снова.';
  }
  switch (error.response.status) {
    case 404:
      return 'Диалог не найден — его переписки нет в вашей организации.';
    case 429:
      return 'Слишком много запросов подряд. Подождите минуту.';
    case 400:
      return 'Диалог не подходит для сборки карточки.';
    default:
      return 'Сервер не принял запрос. Попробуйте ещё раз.';
  }
}

export function useRefreshSummary(): ReturnType<typeof useMutation<void, Error, ConversationSource>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (source: ConversationSource) => {
      await client.post('/conversations/summary/refresh', source);
    },
    onSuccess: (_data, source) => {
      void qc.invalidateQueries({
        queryKey: ['conversation-summary', source.sourceType, source.sourceId],
      });
    },
  });
}
